import axios from 'axios';
import type {
  AdminDashboardData,
  AdminSettings,
  AdminUserListResponse,
  ApiItemResponse,
  ApiListResponse,
  Inspection,
  Job,
  NotificationPreferences,
  Plan,
  Property,
  Report,
  ReportPreset,
  ReportSchema,
  Subscription,
  SubscriptionPlan,
  User,
  Photo,
} from '../types';

const baseURL = import.meta.env.VITE_API_URL ?? '/api';

type AuthResponse = {
  token: string;
  refreshToken?: string;
  user: User;
};

export const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

let currentToken: string | null = null;

export function setAuthToken(token: string): void {
  currentToken = token;
  api.defaults.headers.common.Authorization = `Bearer ${token}`;
}

export function clearAuthToken(): void {
  currentToken = null;
  delete api.defaults.headers.common.Authorization;
}

export function getAuthToken(): string | null {
  return currentToken;
}

// -------- Auth --------
export async function signupUser(payload: { name: string; email: string; password: string }): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>('/auth/signup', payload);
  return response.data;
}

export async function loginUser(payload: { email: string; password: string }): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>('/auth/login', payload);
  return response.data;
}

export async function fetchCurrentUser(): Promise<User> {
  const response = await api.get<{ user: User }>('/auth/me');
  return response.data.user;
}

export async function forgotPassword(email: string): Promise<void> {
  await api.post('/auth/forgot-password', { email });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await api.post(`/auth/reset-password?token=${encodeURIComponent(token)}`, { password });
}

export async function updateOnboarding(payload: {
  completed?: boolean;
  step?: number;
  version?: number;
  lastInspectionId?: string;
  lastSeenAt?: string;
}): Promise<void> {
  await api.patch('/account/onboarding', payload);
}

// -------- Properties & Inspections --------
export async function fetchProperties(): Promise<Property[]> {
  const response = await api.get<ApiListResponse<Property>>('/properties');
  return response.data.data;
}

export async function createProperty(payload: {
  name: string;
  referenceCode?: string;
  address: Property['address'];
  metadata?: Record<string, unknown>;
}): Promise<Property> {
  const response = await api.post<ApiItemResponse<Property>>('/properties', payload);
  return response.data.data;
}

export async function fetchInspections(params: Partial<{ status: string; propertyId: string }>): Promise<Inspection[]> {
  const response = await api.get<ApiListResponse<Inspection>>('/inspections', { params });
  return response.data.data;
}

export async function createInspection(payload: {
  propertyId: string;
  scheduledFor?: string;
  rooms?: Array<{ name: string; displayOrder?: number }>;
  reportPresetId?: string;
}): Promise<Inspection> {
  const response = await api.post<ApiItemResponse<Inspection>>('/inspections', payload);
  return response.data.data;
}

export async function addInspectionRoom(
  inspectionId: string,
  payload: {
    name: string;
    displayOrder?: number;
  }
): Promise<Inspection> {
  const response = await api.post<ApiItemResponse<Inspection>>(`/inspections/${inspectionId}/rooms`, payload);
  return response.data.data;
}

type UploadInspectionPhotosResult = {
  photos: Photo[];
  job?: {
    id: string;
    status: string;
    progress: number;
    queueDepth?: number;
  };
};

export async function uploadInspectionPhotos(
  inspectionId: string,
  formDataOrPayload: FormData | {
    roomId?: string;
    photos: File[];
    reportPresetId?: string;
  }
): Promise<UploadInspectionPhotosResult> {
  let formData: FormData;
  
  if (formDataOrPayload instanceof FormData) {
    // Already a FormData object (from bulk uploader)
    formData = formDataOrPayload;
  } else {
    // Build FormData from payload object
    formData = new FormData();
    if (formDataOrPayload.roomId) {
      formData.append('roomId', formDataOrPayload.roomId);
    }
    if (formDataOrPayload.reportPresetId) {
      formData.append('reportPresetId', formDataOrPayload.reportPresetId);
    }
    formDataOrPayload.photos.forEach((file) => {
      formData.append('photos', file);
    });
  }

  const response = await api.post<{ data: Photo[]; job?: UploadInspectionPhotosResult['job'] }>(
    `/inspections/${inspectionId}/photos`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return {
    photos: response.data.data,
    job: response.data.job,
  };
}

// Presigned URL upload flow - Step 1
export async function getPhotoUploadUrls(
  inspectionId: string,
  payload: { files: Array<{ filename: string; contentType?: string }> }
): Promise<{
  inspectionId: string;
  uploads: Array<{
    fileId: string;
    filename: string;
    key: string;
    uploadUrl: string;
    expiresIn: number;
  }>;
}> {
  const response = await api.post<{
    data: {
      inspectionId: string;
      uploads: Array<{
        fileId: string;
        filename: string;
        key: string;
        uploadUrl: string;
        expiresIn: number;
      }>;
    };
  }>(`/inspections/${inspectionId}/photos/upload-urls`, payload);
  return response.data.data;
}

// Presigned URL upload flow - Step 2
export async function registerUploadedPhotos(
  inspectionId: string,
  payload: {
    roomId?: string;
    photos: Array<{
      key: string;
      filename: string;
      fileSize?: number;
      contentType?: string;
    }>;
  }
): Promise<UploadInspectionPhotosResult> {
  const response = await api.post<{ data: Photo[]; job?: UploadInspectionPhotosResult['job'] }>(
    `/inspections/${inspectionId}/photos/register`,
    payload
  );
  return {
    photos: response.data.data,
    job: response.data.job,
  };
}

export async function fetchInspection(id: string): Promise<Inspection> {
  const response = await api.get<ApiItemResponse<Inspection>>(`/inspections/${id}`);
  return response.data.data;
}

export interface PhotoUrl {
  photoId: string;
  roomId: string;
  url: string;
  filename: string;
}

export async function fetchPhotoUrls(inspectionId: string): Promise<PhotoUrl[]> {
  const response = await api.get<{ data: PhotoUrl[] }>(`/inspections/${inspectionId}/photos/urls`);
  return response.data.data;
}

export async function completeInspection(id: string): Promise<Inspection> {
  const response = await api.post<ApiItemResponse<Inspection>>(`/inspections/${id}/complete`);
  return response.data.data;
}

export interface ReportWithExpiry extends Report {
  expiryInfo: {
    expired: boolean;
    expiresAt: string;
    daysRemaining: number;
    hoursRemaining: number;
  } | null;
  property: Property | null;
  inspection: {
    _id: string;
    status: string;
    roomsCount: number;
  } | null;
  updatedAt: string;
  currentVersion: number;
}

export interface ReportsListResponse {
  success: boolean;
  data: ReportWithExpiry[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ReportDownloadResponse {
  downloadUrl: string;
  version: number;
  filename: string;
  generatedAt: string;
  expiresAt: string;
  daysRemaining: number;
  hoursRemaining: number;
  expiryMessage: string;
}

export async function fetchReports(params?: { page?: number; limit?: number }): Promise<ReportsListResponse> {
  const response = await api.get<ReportsListResponse>('/reports', { params });
  return response.data;
}

export async function fetchReport(inspectionId: string): Promise<Report> {
  const response = await api.get<ApiItemResponse<Report>>(`/reports/inspection/${inspectionId}`);
  return response.data.data;
}

export async function generateReport(payload: {
  inspectionId: string;
  introduction?: string;
  conclusion?: string;
}): Promise<Report> {
  // Don't send inspectionId in body - it's already in the URL path
  const { inspectionId, ...bodyPayload } = payload;
  const response = await api.post<ApiItemResponse<Report>>(`/reports/inspection/${inspectionId}`, bodyPayload);
  return response.data.data;
}

export async function downloadReport(reportId: string, version?: number): Promise<ReportDownloadResponse> {
  const params = version ? { version } : {};
  const response = await api.get<ApiItemResponse<ReportDownloadResponse>>(`/reports/${reportId}/download`, { params });
  return response.data.data;
}

export async function generateReportPDF(reportId: string): Promise<ReportDownloadResponse> {
  const response = await api.post<{ success: boolean; data: ReportDownloadResponse }>(`/reports/${reportId}/generate-pdf`);
  return response.data.data;
}

// -------- Subscription --------
export async function fetchSubscription(organizationId: string): Promise<Subscription> {
  const response = await api.get<ApiItemResponse<Subscription>>(`/subscriptions/${organizationId}`);
  return response.data.data;
}

export async function upgradeSubscription(organizationId: string): Promise<Subscription> {
  const response = await api.post<ApiItemResponse<Subscription>>(`/subscriptions/${organizationId}/upgrade`);
  return response.data.data;
}

export async function fetchSubscriptionPlans(): Promise<SubscriptionPlan[]> {
  const response = await api.get<{ data: SubscriptionPlan[] }>('/subscriptions/plans');
  return response.data.data;
}

export async function fetchPublicPlans(): Promise<Plan[]> {
  const response = await api.get<{ data: Plan[] }>('/plans/public');
  return response.data.data;
}

export async function createCheckoutSession(
  organizationId: string,
  payload: { priceId: string; cancelUrl?: string }
): Promise<string> {
  const response = await api.post<{ url: string }>(`/subscriptions/${organizationId}/checkout`, payload);
  return response.data.url;
}

export async function openBillingPortal(organizationId: string, payload?: { returnUrl?: string }): Promise<string> {
  const response = await api.post<{ url: string }>(`/subscriptions/${organizationId}/portal`, payload ?? {});
  return response.data.url;
}

export async function cancelSubscription(organizationId: string): Promise<Subscription> {
  const response = await api.post<ApiItemResponse<Subscription>>(`/subscriptions/${organizationId}/cancel`);
  return response.data.data;
}

export async function resumeSubscription(organizationId: string): Promise<Subscription> {
  const response = await api.post<ApiItemResponse<Subscription>>(`/subscriptions/${organizationId}/resume`);
  return response.data.data;
}

// -------- Admin --------
export async function fetchAdminDashboard(): Promise<AdminDashboardData> {
  const response = await api.get<{ data: AdminDashboardData }>('/admin/dashboard');
  return response.data.data;
}

export async function fetchAdminUsers(params: Partial<{ email: string; status: string; isAdmin: 'true' | 'false' | 'both'; page: number; limit: number }>): Promise<AdminUserListResponse> {
  const response = await api.get<AdminUserListResponse>('/admin/users', { params });
  return response.data;
}

export async function fetchAdminSettings(): Promise<AdminSettings> {
  const response = await api.get<{ data: AdminSettings }>('/admin/settings');
  return response.data.data;
}

export async function updateAdminSetting<T extends Record<string, unknown>>(key: string, payload: T): Promise<AdminSettings> {
  const response = await api.put<{ data: AdminSettings }>(`/admin/settings/${key}`, payload);
  return response.data.data;
}

export async function fetchAdminPlans(): Promise<Plan[]> {
  const response = await api.get<{ data: Plan[] }>('/admin/plans');
  return response.data.data;
}

export async function createAdminPlan(payload: Partial<Plan>): Promise<Plan> {
  const response = await api.post<{ data: Plan }>('/admin/plans', payload);
  return response.data.data;
}

export async function assignPlanToOrganization(planId: string, organizationId: string): Promise<Subscription> {
  const response = await api.post<ApiItemResponse<Subscription>>(`/admin/plans/${planId}/assign`, { organizationId });
  return response.data.data;
}

// -------- Account / Notifications --------
export async function fetchNotificationPreferences(): Promise<NotificationPreferences> {
  const response = await api.get<{ data: NotificationPreferences }>('/account/preferences');
  return response.data.data;
}

export async function updateNotificationPreferences(
  payload: Partial<NotificationPreferences>
): Promise<NotificationPreferences> {
  const response = await api.put<{ data: NotificationPreferences }>('/account/preferences', payload);
  return response.data.data;
}

// -------- Report Presets --------
type CreateReportPresetPayload = {
  name: string;
  description?: string;
  schema?: ReportSchema;
  tags?: string[];
  sampleReport?: File | null;
  isDefault?: boolean;
};

type UpdateReportPresetPayload = {
  name?: string;
  description?: string;
  schema?: ReportSchema;
  tags?: string[];
  isDefault?: boolean;
};

export async function fetchReportPresets(): Promise<ReportPreset[]> {
  const response = await api.get<ApiListResponse<ReportPreset>>('/report-presets');
  return response.data.data;
}

export async function createReportPreset(payload: CreateReportPresetPayload): Promise<ReportPreset> {
  const formData = new FormData();
  formData.append('name', payload.name);
  if (payload.description) {
    formData.append('description', payload.description);
  }
  if (payload.schema) {
    formData.append('schema', JSON.stringify(payload.schema));
  }
  if (payload.tags && payload.tags.length > 0) {
    formData.append('tags', JSON.stringify(payload.tags));
  }
  if (payload.sampleReport) {
    formData.append('sampleReport', payload.sampleReport);
  }
  if (typeof payload.isDefault === 'boolean') {
    formData.append('isDefault', String(payload.isDefault));
  }

  const response = await api.post<ApiItemResponse<ReportPreset>>('/report-presets', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data.data;
}

export async function updateReportPreset(presetId: string, payload: UpdateReportPresetPayload): Promise<ReportPreset> {
  const response = await api.patch<ApiItemResponse<ReportPreset>>(`/report-presets/${presetId}`, payload);
  return response.data.data;
}

export async function deleteReportPreset(presetId: string): Promise<void> {
  await api.delete(`/report-presets/${presetId}`);
}

// -------- Jobs --------
export async function fetchInspectionJobs(inspectionId: string): Promise<Job[]> {
  const response = await api.get<ApiListResponse<Job>>(`/jobs/inspection/${inspectionId}`);
  return response.data.data;
}

export async function fetchJob(jobId: string): Promise<Job> {
  const response = await api.get<ApiItemResponse<Job>>(`/jobs/${jobId}`);
  return response.data.data;
}
